import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';

export interface VKUser {
  id: number;
  first_name: string;
  last_name: string;
  photo_200?: string;
  screen_name?: string;
}

interface AuthState {
  user: VKUser | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
}

interface AuthContextType extends AuthState {
  loginWithVK: () => void;
  handleVKCallback: (hash: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const VK_APP_ID = import.meta.env.VITE_VK_APP_ID || '5335123';
const VK_REDIRECT_URI = import.meta.env.VITE_VK_REDIRECT_URI || `${window.location.origin}/auth/vk/callback`;

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    user: null,
    token: null,
    isAuthenticated: false,
    isLoading: true
  });

  useEffect(() => {
    const saved = localStorage.getItem('vk_auth');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setState({ user: parsed.user, token: parsed.token, isAuthenticated: true, isLoading: false });
      } catch {
        localStorage.removeItem('vk_auth');
        setState((s) => ({ ...s, isLoading: false }));
      }
    } else {
      setState((s) => ({ ...s, isLoading: false }));
    }
  }, []);

  const loginWithVK = useCallback(() => {
    const params = new URLSearchParams({
      client_id: VK_APP_ID,
      redirect_uri: VK_REDIRECT_URI,
      display: 'page',
      response_type: 'token',
      v: '5.199',
      scope: 'photo_200,offline'
    });
    window.location.href = `https://oauth.vk.com/authorize?${params.toString()}`;
  }, []);

  const handleVKCallback = useCallback(async (hash: string) => {
    const params = new URLSearchParams(hash.replace(/^\?/, ''));
    const accessToken = params.get('access_token');
    const userId = params.get('user_id');

    if (!accessToken || !userId) {
      throw new Error('VK auth failed: no token');
    }

    const response = await fetch(
      `https://api.vk.com/method/users.get?user_ids=${userId}&fields=photo_200,screen_name&access_token=${accessToken}&v=5.199`
    );
    const data = await response.json();

    if (data.response && data.response.length > 0) {
      const vkUser = data.response[0];
      const authData = { user: vkUser, token: accessToken };
      localStorage.setItem('vk_auth', JSON.stringify(authData));
      setState({ user: vkUser, token: accessToken, isAuthenticated: true, isLoading: false });
    } else {
      throw new Error('VK API returned no user data');
    }
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('vk_auth');
    setState({ user: null, token: null, isAuthenticated: false, isLoading: false });
  }, []);

  return (
    <AuthContext.Provider value={{ ...state, loginWithVK, handleVKCallback, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
